import { useMemo } from 'react'
import { useAccountPageUIContext } from '../AccountPageProvider'
import { AccountCustomPage, AccountPageConfig, AccountPageRealEstate } from '../types'

// Types that belong to pages we render ourselves. Components with these types are
// meant for slots inside those pages, so they never create a custom page.
export const KNOWN_ACCOUNT_PAGES = ['PROFILE', 'ORDERS', 'REWARDS']

const IGNORED_TYPES = ['', 'NONE']

// LOYALTY_CLUB → "Loyalty Club"
const toPageLabel = (type: string) =>
  type
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

/**
 * Pages to create from custom components whose type is not a known page, in
 * config order. Components sharing a type are joined into one page.
 */
export const getAccountCustomPages = (uiConfig?: AccountPageConfig | null): AccountCustomPage[] => {
  const pages: AccountCustomPage[] = []
  for (const component of uiConfig?.custom_component_config?.custom_components ?? []) {
    const type = component?.type?.trim() ?? ''
    if (!component?.enabled || !component?.html_text_string) continue
    if (IGNORED_TYPES.includes(type.toUpperCase()) || KNOWN_ACCOUNT_PAGES.includes(type.toUpperCase())) continue

    const existing = pages.find(page => page.type === type)
    if (existing) {
      existing.html += component.html_text_string
    } else {
      pages.push({ type, label: toPageLabel(type), html: component.html_text_string })
    }
  }
  return pages
}

/** Custom pages for the current config. Never returns pages in the dashboard so merchant scripts don't run in the editor. */
export const useAccountCustomPages = (): AccountCustomPage[] => {
  const { uiConfig, realEstate } = useAccountPageUIContext()
  const components = uiConfig?.custom_component_config?.custom_components

  return useMemo(
    () => (realEstate === AccountPageRealEstate.DASHBOARD ? [] : getAccountCustomPages(uiConfig)),
    [components, realEstate] // eslint-disable-line react-hooks/exhaustive-deps
  )
}
